import * as React from 'react';
import { cn } from '@/lib/utils';

const PageTitleContext = React.createContext<{
  title: React.ReactNode;
  setTitle: (t: React.ReactNode) => void;
}>({ title: null, setTitle: () => {} });

export function PageTitleProvider({ children }: { children: React.ReactNode }) {
  const [title, setTitle] = React.useState<React.ReactNode>(null);
  return (
    <PageTitleContext.Provider value={{ title, setTitle }}>{children}</PageTitleContext.Provider>
  );
}

export function usePageTitle() {
  return React.useContext(PageTitleContext).title;
}

export interface PageHeaderAction {
  key?: string;
  node: React.ReactNode;
}

interface PageHeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode | PageHeaderAction[];
  className?: string;
}

export function PageHeader({ title, description, actions, className }: PageHeaderProps) {
  const { setTitle } = React.useContext(PageTitleContext);
  React.useEffect(() => {
    setTitle(title);
    return () => setTitle(null);
  }, [title, setTitle]);

  const rendered = Array.isArray(actions)
    ? actions.map((a, i) => <React.Fragment key={a.key ?? i}>{a.node}</React.Fragment>)
    : actions;

  if (!description && !rendered) return null;

  return (
    <div
      className={cn(
        'flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      {description ? (
        <p className="text-sm text-muted-foreground">{description}</p>
      ) : (
        <span />
      )}
      {rendered && <div className="flex flex-wrap gap-2">{rendered}</div>}
    </div>
  );
}
